import React from 'react';
import { Column } from 'react-table';
import SessionTable, {
  ResponseGuard,
  SessionTableInstanceProps
} from '../index';
import { CustomSessionCell, RotatingQuestionCell } from '../../cells';
import Tooltip from '../../../tooltip';
import Icon from '../../../../atoms/icon';
import { faInfo } from '@fortawesome/pro-solid-svg-icons';

import {
  formatFlag as formatHeader,
  ICustomProgram,
  ICustomSession,
  getRotatingQuestionNode
} from '../../../../../libs/composer';

type ParsedProgramSource = {
  export?: {
    fields?: Array<{ key: string; label?: string }>;
  };
  ui?: {
    columnLabels?: Record<string, string>;
    conditionalColumns?: Array<{
      key: string;
      showWhen?: Record<string, any>;
    }>;
  };
  links?: any;
};

/**
 * Formats a flow type value for display.
 * Converts values like "regular", "recheck", "week1day1" to readable format.
 *
 * @param flowType - The flow type value from session._data.subset
 * @returns Formatted flow type string
 */
const formatFlowType = (flowType: string | undefined | null): string => {
  if (!flowType) {
    return 'Regular';
  }
  // Replace underscores with spaces and capitalize
  return flowType
    .replace(/_/g, ' ')
    .replace(/([a-z])([A-Z])/g, '$1 $2') // Insert space between camelCase
    .replace(/\b\w/g, (char) => char.toUpperCase()); // Capitalize first letter of each word
};

/**
 * Extracts the data keys from the schema-based program source.
 *
 * @param source - The source object from the schema-based program
 * @returns The list of data keys
 */
const getDataKeysFromSource = (source: ICustomProgram['source']): string[] => {
  if (!source) {
    return [];
  }
  const keys = new Set<string>();
  let schema: ParsedProgramSource;
  try {
    schema = JSON.parse(source);
  } catch {
    return [];
  }
  if (Array.isArray(schema?.export?.fields) && schema.export.fields.length) {
    for (const field of schema.export.fields) {
      if (field?.key) {
        keys.add(field.key);
      }
    }
    return Array.from(keys);
  }
  let subsets = null;
  if (!Array.isArray(schema.links) && typeof schema.links === 'object') {
    subsets = Object.keys(schema.links);
  }
  if (subsets !== null) {
    for (const subset of subsets) {
      for (const link of schema.links[subset]) {
        if (link.store && typeof link.store === 'object') {
          for (const key of Object.keys(link.store)) {
            keys.add(key);
          }
        }
      }
    }
  } else {
    for (const link of schema.links) {
      if (link.store && typeof link.store === 'object') {
        for (const key of Object.keys(link.store)) {
          keys.add(key);
        }
      }
    }
  }
  return Array.from(keys);
};

/**
Extracts additional _data keys observed in session rows.

@param sessions session rows currently loaded for the table page
@returns additional keys not guaranteed by schema export metadata
 */
const getDataKeysFromSessions = (sessions: ICustomSession[]): string[] => {
  const keys = new Set<string>();
  for (const session of sessions || []) {
    if (!session?._data || typeof session._data !== 'object') {
      continue;
    }
    for (const key of Object.keys(session._data)) {
      if (key === 'subset') {
        continue;
      }
      keys.add(key);
    }
  }
  return Array.from(keys);
};

/**
 * Formats a data key into a user-facing column label.
 *
 * @param source - The source object from the custom program
 * @param key - The data key
 * @returns Human-friendly display label
 */
const getColumnLabel = (
  source: ICustomProgram['source'],
  key: string
): string => {
  if (!source) {
    return formatHeader(key);
  }
  try {
    const schema: ParsedProgramSource = JSON.parse(source);
    const labelFromUi = schema?.ui?.columnLabels?.[key];
    if (labelFromUi) {
      return labelFromUi;
    }
    const labelFromExport = schema?.export?.fields?.find(
      (field) => field.key === key
    )?.label;
    if (labelFromExport) {
      return labelFromExport;
    }
  } catch {
    // Ignore and fall back
  }
  return formatHeader(key);
};

/**
 * Evaluates whether a schema conditional column should be visible.
 *
 * @param showWhen - Conditional display metadata
 * @param sessions - Session data to evaluate against
 * @returns True when the column should be visible
 */
const shouldShowConditionalColumn = (
  showWhen: Record<string, any> | undefined,
  sessions: ICustomSession[]
): boolean => {
  if (!showWhen) {
    return true;
  }
  const key = showWhen.key;
  const operator = showWhen.operator || 'exists';
  const value = showWhen.value;
  if (!key) {
    return true;
  }
  return sessions.some((session) => {
    const dataValue = session?._data?.[key as keyof typeof session._data];
    if (operator === 'exists') {
      return dataValue != null;
    }
    if (operator === '==') {
      return dataValue === value;
    }
    if (operator === '!=') {
      return dataValue !== value;
    }
    if (operator === '>') {
      return Number(dataValue) > Number(value);
    }
    if (operator === '>=') {
      return Number(dataValue) >= Number(value);
    }
    if (operator === '<') {
      return Number(dataValue) < Number(value);
    }
    if (operator === '<=') {
      return Number(dataValue) <= Number(value);
    }
    return true;
  });
};

/**
 * Resolves whether a key should be shown based on ui.conditionalColumns metadata.
 *
 * @param source - The source object from the custom program
 * @param key - The data key
 * @param sessions - Session list in the current table page
 * @returns True when the key should be rendered
 */
const shouldShowColumn = (
  source: ICustomProgram['source'],
  key: string,
  sessions: ICustomSession[]
): boolean => {
  if (!source) {
    return true;
  }
  try {
    const schema: ParsedProgramSource = JSON.parse(source);
    const conditionalColumns = schema?.ui?.conditionalColumns;
    if (!Array.isArray(conditionalColumns) || conditionalColumns.length === 0) {
      return true;
    }
    const config = conditionalColumns.find((column) => column.key === key);
    if (!config) {
      return true;
    }
    return shouldShowConditionalColumn(config.showWhen, sessions);
  } catch {
    return true;
  }
};

/**
 * Checks if a store key corresponds to a rotating question.
 * Looks for links from the rotating question node that store to this key.
 *
 * @param source - The source object from the schema-based program
 * @param key - The store key to check
 * @returns True if the key corresponds to a rotating question
 */
const isRotatingQuestionKey = (
  source: ICustomProgram['source'],
  key: string
): boolean => {
  if (!source) {
    return false;
  }
  try {
    const rotatingNode = getRotatingQuestionNode(source);
    if (!rotatingNode) {
      return false;
    }
    const schema = JSON.parse(source);
    let linksToCheck: any[] = [];
    if (!Array.isArray(schema.links) && typeof schema.links === 'object') {
      // Multiple flow types
      for (const subset of Object.keys(schema.links)) {
        linksToCheck = linksToCheck.concat(schema.links[subset]);
      }
    } else {
      linksToCheck = schema.links || [];
    }
    // Check if any link from the rotating node stores to this key
    for (const link of linksToCheck) {
      if (
        link.from === rotatingNode.nodeKey &&
        link.store &&
        typeof link.store === 'object' &&
        Object.keys(link.store).includes(key)
      ) {
        return true;
      }
    }
  } catch {
    // Silently fail
  }
  return false;
};

const GenericTable: React.FC<SessionTableInstanceProps> = (
  props
): JSX.Element => {
  const source: ICustomProgram['source'] = props.customProgram?.source;
  const schemaDataKeys = getDataKeysFromSource(source);

  const getColumns = (sessions: ICustomSession[]): Column<ICustomSession>[] => {
    const columns: Column<ICustomSession>[] = [];
    const observedDataKeys = getDataKeysFromSessions(sessions);
    const customDataKeys = schemaDataKeys.concat(
      observedDataKeys.filter((key) => !schemaDataKeys.includes(key))
    );
    columns.push({
      Header: (
        <Tooltip content="The flow type determines which set of questions and logic are used for this session. For schema-based programs with multiple flow types (like time-based flows), this shows which specific flow was used (e.g., 'regular', 'week1day1', 'monthly').">
          <Icon icon={faInfo} />
          <span className="ml-4">Flow Type</span>
        </Tooltip>
      ),
      id: 'flowType',
      Cell: ResponseGuard(({ row: { original: session } }) => {
        const flowType = session._data?.subset as string | undefined | null;
        const formatted = formatFlowType(flowType);
        return <span>{formatted}</span>;
      })
    });
    for (const key of customDataKeys) {
      if (!shouldShowColumn(source, key, sessions)) {
        continue;
      }
      const isRotating = source ? isRotatingQuestionKey(source, key) : false;
      columns.push({
        Header: getColumnLabel(source, key),
        id: key,
        Cell: ResponseGuard(
          isRotating
            ? RotatingQuestionCell(
                key as keyof ICustomSession,
                props.customProgram
              )
            : CustomSessionCell(key as keyof ICustomSession)
        )
      });
    }

    const hasProgramComment = sessions.some((session) => {
      const comment = session?._data?.programComment;
      return typeof comment === 'string' && comment.trim().length > 0;
    });
    if (hasProgramComment) {
      columns.push({
        Header: 'Program Comment',
        id: 'programComment',
        Cell: ResponseGuard(({ row: { original: session } }) => {
          const comment = session?._data?.programComment;
          if (typeof comment !== 'string' || comment.trim().length === 0) {
            return <span>--</span>;
          }
          return <span>{comment}</span>;
        })
      });
    }
    return columns;
  };

  return (
    <div style={{ height: '100%' }}>
      <style>{`
        .session-table-container .bp3-card {
          max-height: 500px;
          overflow-y: auto;
        }
      `}</style>
      <div className="session-table-container">
        <SessionTable<ICustomSession>
          url="/composer/data/"
          getColumns={getColumns}
          optionalColumns={[
            {
              id: 'flowType',
              label: 'Flow Type',
              defaultVisible: false
            }
          ]}
          {...props}
        />
      </div>
    </div>
  );
};

export default GenericTable;
